import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Camera, Upload, Trash2 } from "lucide-react";
import { uploadFile, MAX_UPLOAD_BYTES } from "../../actions/uploads";

export default function ImageUpload({
  value,
  onChange,
  fallback,
  hint,
  alt,
  square = false,
  accept = "image/png, image/jpeg, image/webp",
  uploadLabel = "Upload photo",
  changeLabel = "Change photo",
  // "hero" puts the avatar beside the card heading, with the buttons under
  // the text rather than under the image.
  layout = "inline",
  children = null,
}) {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  const shapeClass = square ? " acct-avatar--square" : "";
  const hero = layout === "hero";

  // The image has to reach the server to be worth anything: the URL is saved
  // on the account and served elsewhere, so a local data URL would only ever
  // exist in this browser tab - and would bloat the PATCH body with the whole
  // file on every save.
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again still fires a change event.
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("That file isn't an image. Choose a JPG or PNG.");
      return;
    }

    // Checked here as well so an oversized file is refused before it is read
    // and base64-encoded. The limit itself lives with the upload action.
    if (file.size > MAX_UPLOAD_BYTES) {
      setError("That image is over 2 MB. Pick a smaller one.");
      return;
    }

    setError("");
    setUploading(true);

    const result = await dispatch(uploadFile(file));

    setUploading(false);

    if (!result?.success) {
      setError(result?.message || "We couldn't upload that image.");
      return;
    }

    onChange(result.url);
  };

  const remove = () => {
    setError("");
    onChange(null);
  };

  const picture = (
    <span className={`acct-avatar-shell${hero ? " is-hero" : ""}`}>
      {value ? (
        <img className={`acct-avatar acct-avatar--img${shapeClass}`} src={value} alt={alt} />
      ) : (
        <span className={`acct-avatar${shapeClass}`}>{fallback}</span>
      )}

      {/* The badge is decoration on top of the real control below, so it is
          not a second focus stop for the same action. */}
      {hero && (
        <span className="acct-avatar-badge" aria-hidden="true">
          <Camera size={14} strokeWidth={2} />
        </span>
      )}
    </span>
  );

  return (
    <div className={`acct-avatar-row${hero ? " is-hero" : ""}`}>
      {picture}

      <div className="acct-avatar-actions">
        {children}

        <input
          type="file"
          accept={accept}
          ref={fileInputRef}
          onChange={handleFile}
          className="acct-file-input"
          aria-hidden="true"
          tabIndex={-1}
        />

        <div className="acct-avatar-buttons">
        <button
          type="button"
          className="acct-btn acct-btn--outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <span className="install-status-spinner" aria-hidden="true" />
          ) : (
            <Upload size={16} strokeWidth={2} />
          )}
          {uploading ? "Uploading..." : value ? changeLabel : uploadLabel}
        </button>

        <button
          type="button"
          className="acct-btn acct-btn--quiet"
          onClick={remove}
          disabled={!value || uploading}
        >
          <Trash2 size={16} strokeWidth={2} />
          Remove
        </button>
        </div>

        {error ? (
          <span className="acct-hint acct-hint--error">{error}</span>
        ) : (
          <span className="acct-hint">{hint}</span>
        )}
      </div>
    </div>
  );
}
